import { useState, type FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth'
import { api, ApiError } from '../api'
import { useI18n, LangToggle } from '../i18n'
import { DisplayModeToggle } from '../displayMode'
import { Alert, Spinner } from '../components/ui'

type Mode = 'password' | 'link'

export default function Login() {
  const { login } = useAuth()
  const { t } = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: string } | null)?.from || '/'

  const [mode, setMode] = useState<Mode>('password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState<'login' | 'link' | null>(null)
  const [error, setError] = useState('')
  const [sent, setSent] = useState<{ to: string; devLink?: string } | null>(null)

  function switchMode(next: Mode) {
    setMode(next)
    setError('')
  }

  async function onLogin(e: FormEvent) {
    e.preventDefault()
    setError('')
    setBusy('login')
    try {
      await login(email.trim().toLowerCase(), password)
      navigate(from, { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('login.error.generic'))
    } finally {
      setBusy(null)
    }
  }

  async function onSendLink(e: FormEvent) {
    e.preventDefault()
    setError('')
    if (!email.trim()) {
      setError(t('login.enterEmailFirst'))
      return
    }
    setBusy('link')
    try {
      const res = await api.requestLink(email.trim().toLowerCase())
      setSent({ to: email.trim().toLowerCase(), devLink: res.devLink })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('login.error.sendLink'))
    } finally {
      setBusy(null)
    }
  }

  if (sent) {
    return (
      <div className="auth-wrap">
        <div className="auth-card">
          <div className="auth-brand">
            <img className="brand-logo" src="/mark.png" alt="Armon Kappeli" />
            <div className="brand-name">Armon Kappeli</div>
          </div>
          <h1>{t('login.checkInbox.title')}</h1>
          <p className="sub">{t('login.checkInbox.body', { email: sent.to })}</p>
          {sent.devLink && (
            <Alert kind="info">
              <a href={sent.devLink}>{t('login.devLink')}</a>
            </Alert>
          )}
          <div className="auth-foot">
            <button className="btn btn-ghost" onClick={() => setSent(null)}>
              {t('login.back')}
            </button>
          </div>
          <div className="auth-lang"><DisplayModeToggle /></div>
          <div className="auth-lang"><LangToggle /></div>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-brand">
          <img className="brand-logo" src="/mark.png" alt="Armon Kappeli" />
          <div className="brand-name">Armon Kappeli</div>
        </div>
        <h1>{t('login.title')}</h1>
        <p className="sub">{t('login.subtitle')}</p>

        <div className="segmented auth-tabs">
          <button className={mode === 'password' ? 'active' : ''} onClick={() => switchMode('password')} type="button">
            {t('login.tab.password')}
          </button>
          <button className={mode === 'link' ? 'active' : ''} onClick={() => switchMode('link')} type="button">
            {t('login.tab.link')}
          </button>
        </div>

        {mode === 'password' ? (
          <form className="auth-form" onSubmit={onLogin}>
            {error && <Alert kind="error">{error}</Alert>}
            <div className="field">
              <label htmlFor="email">{t('login.email')}</label>
              <input
                id="email"
                className="input"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="password">{t('login.password')}</label>
              <input
                id="password"
                className="input"
                type="password"
                autoComplete="current-password"
                placeholder={t('login.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={busy !== null}>
              {busy === 'login' ? <Spinner /> : t('login.signIn')}
            </button>
            <p className="hint mt-16 center">{t('login.noPasswordHint')}</p>
          </form>
        ) : (
          <form className="auth-form" onSubmit={onSendLink}>
            {error && <Alert kind="error">{error}</Alert>}
            <div className="field">
              <label htmlFor="email-link">{t('login.email')}</label>
              <input
                id="email-link"
                className="input"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <button className="btn btn-purple btn-lg btn-block" type="submit" disabled={busy !== null}>
              {busy === 'link' ? <Spinner /> : t('login.sendLink')}
            </button>
            <p className="hint mt-16 center">{t('login.linkHint')}</p>
          </form>
        )}
        <div className="auth-lang"><DisplayModeToggle /></div>
        <div className="auth-lang"><LangToggle /></div>
      </div>
    </div>
  )
}
